import axios from "@/axios";

import formatError from "./formatError";
import { defineStore } from "pinia";
import { useStorage } from "@vueuse/core"
import bus from "../bus";

const defaultData = {
  id: null,
  role: null,
  firstname: null,
  lastname: null,
  email: null,
  towns_data: [],
};

function validateCaptcha(valid) {
  return valid ? {} : { diov: "email@domain.fr" };
}
export const useUserStore = defineStore("user", {
  state: () => {
    return {
      data: useStorage("data", defaultData, localStorage, { mergeDefaults: true },),
      isLoggedIn: useStorage("isloggedin", false, localStorage, { mergeDefaults: true },),
    }
  },
  getters: {
    getData: ({
      data: {
        id,
        firstname,
        lastname,
        phone,
        email,
        towns_data: townsData,
        role,
      },
    }) => ({
      id,
      firstname,
      lastname,
      phone,
      email,
      townsData,
      role,
    }),
    /**
     * @returns {string}
     */
    id() {
      return this.getData.id;
    },
    /**
     * @returns {string}
     */
    role() {
      return this.data.role;
    },
    /**
     * @returns {boolean}
     */
    loggedIn(state) {
      return this.id && state.isLoggedIn;
    },
    /**
     * @returns {boolean}
     */
    isAdmin() {
      return this.loggedIn && this.role === "Admin";
    },
    isAdminFileDownloader() {
      return this.loggedIn && this.role === "AdminFileDownloader";
    },
    /**
     * @returns {boolean}
     */
    isUser() {
      return this.loggedIn && (this.role === "User" || this.role === "Updated");
    },
  },
  actions: {
    setUserInfo(info) {
      this.data = {
        ...this.data,
        ...info,
      };
      this.isLoggedIn = true;
    },
    clearUserInfo() {
      this.data = {
        ...defaultData,
      };
      this.isLoggedIn = false;
    },
    clear() {
      this.clearUserInfo();
      window.location.reload();
    },
    async checkLoginToken() {
      if (!this.isLoggedIn) {
        return null;
      }
      try {
        const {
          status,
          data: { data },
        } = await axios.get(`/api/users/${this.id}`);
        if (status === 200) {
          this.setUserInfo(data);
        } else {
          throw formatError(
            `Unsupported status code after getting user info: ${status}`,
            status
          );
        }
      } catch (e) {
        if (e.response && e.response.status === 401) {
          this.clearUserInfo();
          return null;
        }
        if (e.response) {
          return e.response;
        }
      }
      return null;
    },
    async logout() {
      this.clearUserInfo();
      try {
        await axios.post("/api/users/logout");
      } catch (e) {
        if (e.response) {
          bus.emit("openModal", { modal: "logout", params: {}});
          return e.response;
        }
      }
      window.location.reload();
      return null;
    },
    async register(
      _,
      {
        firstname,
        lastname,
        phone,
        email,
        password,
        istat,
        beneficiaryName,
        beneficiaryAddress,
        beneficiaryCity,
        bankName,
        swiftRoutingNumber,
        ibanNumber,
        towns,
        valid,
        postHeld,
        zipOfOrganization,
      }
    ) {
      try {
        await axios.post("/api/users/register", {
          firstname,
          lastname,
          phone,
          email,
          password,
          istat,
          beneficiary_name: beneficiaryName,
          beneficiary_address: beneficiaryAddress,
          beneficiary_city: beneficiaryCity,
          bank_name: bankName,
          swift_routing_number: swiftRoutingNumber,
          iban_number: ibanNumber,
          post_held: postHeld,
          zip_of_organization: zipOfOrganization,
          towns: towns.length === 0 ? undefined : towns,
          ...validateCaptcha(valid),
        });
        return null;
      } catch (e) {
        if (e.response) {
          return e.response;
        }
        throw e;
      }
    },
    async login({ email, password, valid }) {
      try {
        const res = await axios.post("/api/users/login", {
          email,
          password,
          ...validateCaptcha(valid),
        });
        const {
          status,
          data: { data },
        } = res;

        if (status === 200) {
          this.setUserInfo(data);
          window.location.reload();
          return null;
        }
        throw formatError(
          `Unsupported status code after logging in: ${status}`,
          status
        );
      } catch (e) {
        if (e.response) {
          // 5xx is an internal server error/ or unrecoverable error
          if (e.response.status >= 500 && e.response.status < 600)
            return 500;
          return e.response;
        }
        throw Error(
          `Unsuported error when requesting data`,
        );
      }
    },

    async contact(
      { firstname, lastname, email, subject, content, towns, valid }
    ) {
      try {
        const { status } = await axios.post("/api/contact", {
          firstname,
          lastname,
          email,
          subject,
          content,
          towns: towns.length === 0 ? undefined : towns.map((town) => town.id),
          ...validateCaptcha(valid),
        });
        if (status === 204) {
          return null;
        }
        throw formatError(`Envoi impossible: code d'erreur: ${status}`, status);
      } catch (e) {
        console.error(e);
        if (e.response) {
          return e.response;
        }
      }
      return null;
    },

    async resetPassword({ email, password, token }) {
      try {
        await axios.post("/api/users/password/reset", {
          email,
          password,
          token,
        });
        return null;
      } catch (e) {
        if (e.response) {
          return e.response;
        }
        console.error(e);
        return null;
      }
    },

    async resetPasswordRequest({ email, valid }) {
      try {
        await axios.post("/api/users/password/forget", {
          email,
          ...validateCaptcha(valid),
        });
        return null;
      } catch (e) {
        if (e.response) {
          return e.response;
        }
        console.error(e);
        return null;
      }
    },

    async modify({
      firstname,
      lastname,
      phone,
      email,
      towns,
    }) {
      if (!this.loggedIn) return null;
      try {
        const { status, data } = await axios.put(`/api/users/${this.id}`, {
          firstname: firstname || undefined,
          lastname: lastname || undefined,
          phone: phone || undefined,
          email: email || undefined,
          new_towns: towns && towns.length > 0 ? towns : undefined,
        });
        if (status !== 200) {
          throw formatError(`Unknown status code: ${status}`);
        }
        this.setUserInfo(data);
        return null;
      } catch (e) {
        if (e.response) return e.response;
        throw e;
      }
    },

    async changePassword({ oldPassword, password }) {
      if (!this.loggedIn) return null;
      try {
        const { status } = await axios.post("/api/users/password/change", {
          password,
          old_password: oldPassword,
        });
        if (status !== 204) {
          throw formatError(`Unknown status code: ${status}`);
        }
        return null;
      } catch (e) {
        if (e.response) return e.response;
        throw e;
      }
    },
  },
});
